// Prevent multiple declarations when script is loaded multiple times
if (!customElements.get('video-carousel')) {

/** Custom event names for the video carousel / modal. */
const ROCKETSTORIES_EVENTS = {
    /** Dispatched when the current video changes (next, prev, or click on another video). */
    VIDEO_CHANGED: 'rocketstories:videochanged',
    /** Dispatched when the video modal (carousel or widget) is closed. */
    VIDEO_MODAL_CLOSED: 'rocketstories:videomodalclosed'
};

class VideoCarousel extends HTMLElement {
    constructor() {
        console.log(
            "%c✦ Prestarocket %cRocketStories %c✦",
            "background: linear-gradient(135deg, #191433 0%, #2d1b4e 100%); color: #fff; font-weight: 700; font-size: 1.4em; padding: 8px 16px; border-radius: 8px 0 0 8px; letter-spacing: 0.05em;",
            "background: linear-gradient(135deg, #ff6b9d 0%, #c44569 100%); color: #fff; font-weight: 700; font-size: 1.4em; padding: 8px 16px; letter-spacing: 0.08em; text-shadow: 0 0 20px rgba(255,107,157,0.5);",
            "background: linear-gradient(135deg, #2d1b4e 0%, #191433 100%); color: #fff; font-weight: 700; font-size: 1.4em; padding: 8px 16px; border-radius: 0 8px 8px 0; letter-spacing: 0.05em;"
        );
        super();
        // Initialize component state (attributes read in connectedCallback)
        this.currentVideoIndex = 0;
        this.videos = [];
        this.shape = 'horizontal';
        this.modalDirection = 'horizontal';
        this.jsonUrl = null;
        this.previewMode = false;
        this.previewAutoplay = 'none';
        this.attachShadow({ mode: 'open' });
        this._modalWidgetActive = false;
        this._hlsLoaded = false;
        this._intersectionObserver = null;
        this._connectionQuality = null;
        this._connectionCheckPromise = null;
        this._isLowPower = undefined;
        this._scrollTimeout = null;
        this._widgetVisible = false;
        this.widgetDelay = 500;
        this.widgetShowOnScroll = false;
        this.widgetRememberClose = false;
        // Global muted state for all videos in this component
        this._globalMutedState = false; // Start with sound on
    }

    // Called when the element is added to the DOM
    connectedCallback() {
        // Read attributes now that element is connected to DOM
        this.shape = this.getAttribute('shape') || 'horizontal';
        this.modalDirection = this.getAttribute('modal-direction') || 'horizontal';
        this.jsonUrl = this.getAttribute('json-url');
        this.previewMode = this.hasAttribute('preview-mode');
        this.previewAutoplay = this.getAttribute('preview-autoplay') || 'none';
        this.widgetDelay = parseInt(this.getAttribute('widget-delay')) || 500;
        this.widgetShowOnScroll = this.hasAttribute('widget-show-on-scroll');
        this.widgetRememberClose = this.hasAttribute('widget-remember-close');
        this.widgetOffsetX = parseInt(this.getAttribute('widget-offset-x')) || 20;
        this.widgetOffsetY = parseInt(this.getAttribute('widget-offset-y')) || 20;
        
        this.render();
        this.setupEventListeners();
        this.collectVideos().then(() => {
            setTimeout(() => {
                this.forceMobileConfiguration();
            }, 100);
            
            // Show widget only after videos are collected
            if (this.isWidgetMode()) {
                if (this.widgetShowOnScroll) {
                    this.setupScrollDetection();
                } else {
                    // Show widget immediately after delay
                    this.hideWidget();
                    setTimeout(() => this.showWidget(), this.widgetDelay);
                }
            }
        });
    }

    // Cleanup event listeners and observers when element is removed
    disconnectedCallback() {
        if (this._widgetKeyHandler) {
            document.removeEventListener('keydown', this._widgetKeyHandler);
        }
        if (this._carouselKeyHandler) {
            document.removeEventListener('keydown', this._carouselKeyHandler);
        }
        if (this._intersectionObserver) {
            this._intersectionObserver.disconnect();
        }
        
        // Cleanup progress tracking
        if (this._progressUpdateHandler) {
            const videoElements = this.shadowRoot.querySelectorAll('video');
            videoElements.forEach(video => {
                video.removeEventListener('timeupdate', this._progressUpdateHandler);
                video.removeEventListener('loadedmetadata', this._progressUpdateHandler);
            });
        }
        
        // Cleanup HLS instances
        const videoElements = this.shadowRoot.querySelectorAll('video');
        videoElements.forEach(video => {
            if (video.hls) {
                video.hls.destroy();
                video.hls = null;
            }
        });
        
        // Cleanup hover event listeners
        const videoItems = this.shadowRoot.querySelectorAll('.video-item');
        videoItems.forEach(item => {
            if (item._hoverEnterHandler) {
                item.removeEventListener('mouseenter', item._hoverEnterHandler);
                item._hoverEnterHandler = null;
            }
            if (item._hoverLeaveHandler) {
                item.removeEventListener('mouseleave', item._hoverLeaveHandler);
                item._hoverLeaveHandler = null;
            }
            if (item._hoverClickHandler) {
                item.removeEventListener('click', item._hoverClickHandler);
                item._hoverClickHandler = null;
            }
        });

        // Cleanup swipe event listeners
        if (this._modalSwipeHandlers) {
            const modalDialog = this.shadowRoot.querySelector('.modal-dialog');
            if (modalDialog) {
                modalDialog.removeEventListener('touchstart', this._modalSwipeHandlers.handleTouchStart);
                modalDialog.removeEventListener('touchmove', this._modalSwipeHandlers.handleTouchMove);
                modalDialog.removeEventListener('touchend', this._modalSwipeHandlers.handleTouchEnd);
            }
        }
        
        // Cleanup scroll detection
        this.cleanupScrollDetection();

        // Cleanup carousel resize handler
        if (this._carouselResizeHandler) {
            window.removeEventListener('resize', this._carouselResizeHandler);
            this._carouselResizeHandler = null;
        }
    }

    /**
     * Check if device is low-power (limited memory, CPU, or battery saver mode)
     * @returns {boolean} true if device appears to be low-power
     */
    isLowPowerDevice() {
        // Cache the result
        if (this._isLowPower !== undefined) {
            return this._isLowPower;
        }

        let isLowPower = false;

        try {
            // Check device memory (< 4GB considered low-power)
            if ('deviceMemory' in navigator && navigator.deviceMemory < 4) {
                isLowPower = true;
            }

            // Check CPU cores (<= 2 cores considered low-power)
            if ('hardwareConcurrency' in navigator && navigator.hardwareConcurrency <= 2) {
                isLowPower = true;
            }

            // Check if user prefers reduced motion (accessibility/battery saver)
            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                isLowPower = true;
            }
        } catch (error) {
            // Silent fail - assume not low-power
        }

        this._isLowPower = isLowPower;
        return isLowPower;
    }

    /**
     * Check connection quality and determine if preview videos should be loaded
     * @returns {boolean} true if connection is good enough for preview videos
     */
    checkConnectionQuality() {
        // If we already know the connection quality, return it
        if (this._connectionQuality !== null) {
            return this._connectionQuality;
        }

        let isGoodConnection = true;

        try {
            // Check if Network Information API is available
            if ('connection' in navigator) {
                const connection = navigator.connection;
                
                // Check if user enabled data saver mode
                if (connection.saveData === true) {
                    isGoodConnection = false;
                }
                
                // Check effective type (4g, 3g, 2g, slow-2g)
                if (connection.effectiveType) {
                    const effectiveType = connection.effectiveType;
                    
                    // Only allow preview videos on 4g or better connections
                    if (effectiveType === 'slow-2g' || effectiveType === '2g' || effectiveType === '3g') {
                        isGoodConnection = false;
                    }
                }
            }

            // Also check if device is low-power
            if (this.isLowPowerDevice()) {
                isGoodConnection = false;
            }
        } catch (error) {
            // If we can't check connection, assume it's good (fallback)
            isGoodConnection = true;
        }

        this._connectionQuality = isGoodConnection;
        return isGoodConnection;
    }

    /**
     * Get optimal video quality based on screen size and connection
     * Shopify/Cloudflare already handle some optimization, this adds client-side logic
     * @returns {string} 'low', 'medium', or 'high'
     */
    getOptimalVideoQuality() {
        const screenWidth = window.screen.width * (window.devicePixelRatio || 1);
        const isGoodConnection = this.checkConnectionQuality();

        // Small screens (mobile) - max 720p
        if (screenWidth <= 750) {
            return 'low';
        }

        // Medium screens (tablet) or poor connection - max 1080p
        if (screenWidth <= 1200 || !isGoodConnection) {
            return 'medium';
        }

        // Large screens with good connection - allow 4K
        return 'high';
    }

    /**
     * Check if component is in widget mode (single video) vs carousel mode (multiple videos)
     * @returns {boolean} True if in widget mode
     */
    isWidgetMode() {
        return this.hasAttribute('widget') || this.getAttribute('mode') === 'widget';
    }

    /**
     * Main render method - delegates to specific render methods based on mode
     */
    render() {
        this.shadowRoot.innerHTML = '';
        if (this.isWidgetMode()) {
            this.renderWidget();
        } else {
            this.renderCarousel();
        }
    }

    /**
     * Render widget mode - single video with modal dialog
     */
    renderWidget() {
        const template = document.getElementById('video-carousel-widget-template');
        if (!template) {
            console.error('[VideoCarousel] Widget template not found!');
            this.shadowRoot.innerHTML = '<span>Widget template not found</span>';
            return;
        }
        this.shadowRoot.appendChild(template.content.cloneNode(true));
        this.updateWidgetContent();
    }

    /**
     * Render carousel mode - multiple videos in a grid/list layout
     */
    renderCarousel() {
        const template = document.getElementById('video-carousel-template');
        if (!template) {
            console.error('Template video-carousel-template not found');
            return;
        }
        this.shadowRoot.appendChild(template.content.cloneNode(true));
        
        this.applyModalDirection();
        this.applyThumbnailStyles();
    }

    /**
     * Update widget content based on JSON data or src/poster attributes
     * Handles mini widget positioning and modal video content with same conditions as previews
     */
    async updateWidgetContent() {
        let src = this.getAttribute('src') || '';
        let poster = this.getAttribute('poster') || '';
        let preview = '';

        // If we have JSON data, use the first video
        if (this.videos && this.videos.length > 0) {
            const firstVideo = this.videos[0];
            src = firstVideo.src || src;
            poster = firstVideo.poster || poster;
            preview = firstVideo.preview || '';
        }

        // Handle mini widget positioning and visibility
        const mini = this.shadowRoot.querySelector('.mini');
        const miniPlaceholder = this.shadowRoot.querySelector('.mini .placeholder');
        const miniVideo = this.shadowRoot.querySelector('.mini-video');
        
        if (mini) {
            // Toggle visibility: show when widget visible and modal not active
            mini.classList.toggle('show', this._widgetVisible && !this._modalWidgetActive);

            // Appliquer la classe de forme à la mini vidéo (sécurisé)
            const allowedShapes = ['bubble', 'vertical', 'horizontal'];
            let shape = allowedShapes.includes(this.shape) ? this.shape : 'vertical';
            const shapeClasses = ['shape-bubble', 'shape-vertical', 'shape-horizontal'];
            mini.classList.remove(...shapeClasses);
            mini.classList.add(`shape-${shape}`);

            // Make widget focusable (always, even when hidden for screen readers)
            mini.tabIndex = this._modalWidgetActive ? -1 : 0;
            mini.setAttribute('role', 'button');
            mini.setAttribute('aria-label', this.getLabel('open'));
            mini.setAttribute('aria-expanded', this._modalWidgetActive.toString());

            // Position widget left or right based on attribute
            const pos = (this.getAttribute('position') || 'left').toLowerCase();
            mini.classList.remove('mini-left', 'mini-right');
            mini.classList.add(pos === 'right' ? 'mini-right' : 'mini-left');
            
            // Apply offset custom properties
            mini.style.setProperty('--widget-offset-x', `${this.widgetOffsetX}px`);
            mini.style.setProperty('--widget-offset-y', `${this.widgetOffsetY}px`);
        }

        // Handle mini widget content - only load when widget appears
        if (miniVideo && miniPlaceholder) {
            // Initially show placeholder, content will be loaded when widget appears
            miniVideo.classList.add('hidden');
            miniPlaceholder.classList.remove('hidden');
        }

        // Handle modal video content - only load when dialog opens
        const modalVideo = this.shadowRoot.querySelector('.modal-dialog.widget-mode .modal-video-main');
        if (modalVideo) {
            // Only hide if no src is set (initial state)
            if (!modalVideo.src) {
                modalVideo.classList.add('hidden');
            } else {
                // If src is set, show the video
                modalVideo.classList.remove('hidden');
            }
        }

        // Show/hide dialog based on widget state
        const dialog = this.shadowRoot.querySelector('.modal-dialog.widget-mode');
        if (dialog && this._modalWidgetActive) {
            dialog.showModal();
        } else if (dialog && !this._modalWidgetActive) {
            dialog.close();
        }
    }

    /**
     * Load HLS.js library dynamically
     * Uses data-hls-url attribute for script URL (configurable from Liquid)
     * @returns {Promise} Promise that resolves when HLS.js is loaded
     */
    async loadHLS() {
        // Already loaded globally
        if (window.Hls) {
            this._hlsLoaded = true;
            return;
        }

        // Already loading - wait for existing promise
        if (VideoCarousel._hlsLoadingPromise) {
            await VideoCarousel._hlsLoadingPromise;
            return;
        }

        // Get URL from data attribute (fallback to CDN)
        const hlsUrl = this.getAttribute('data-hls-url') 
            || 'https://cdn.jsdelivr.net/npm/hls.js@latest/dist/hls.min.js';

        // Check if script tag already exists
        const existingScript = document.querySelector(`script[src="${hlsUrl}"]`);
        if (existingScript) {
            // Wait for existing script to load
            if (window.Hls) {
                this._hlsLoaded = true;
                return;
            }
            await new Promise((resolve) => {
                existingScript.addEventListener('load', resolve, { once: true });
            });
            this._hlsLoaded = true;
            return;
        }

        // Create loading promise (shared across all instances)
        VideoCarousel._hlsLoadingPromise = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = hlsUrl;
            script.async = true;
            script.crossOrigin = 'anonymous';
            
            script.addEventListener('load', () => {
                this._hlsLoaded = true;
                VideoCarousel._hlsLoadingPromise = null;
                resolve();
            }, { once: true });
            
            script.addEventListener('error', () => {
                VideoCarousel._hlsLoadingPromise = null;
                reject(new Error(`Failed to load HLS.js from ${hlsUrl}`));
            }, { once: true });
            
            document.head.appendChild(script);
        });

        await VideoCarousel._hlsLoadingPromise;
    }

    /**
     * Setup HLS player for a video element
     * @param {HTMLVideoElement} videoElement - The video element to setup
     * @param {string} src - The HLS stream URL
     */
    setupHLSPlayer(videoElement, src) {
        // Check if HLS is already configured for this video
        if (videoElement.dataset.hlsConfigured === 'true' && videoElement.src === src) {
            return; // Already configured with the same source
        }

        // Configure video for mobile (prevent fullscreen)
        this.configureVideoForMobile(videoElement);

        if (window.Hls && window.Hls.isSupported()) {
            // Destroy existing HLS instance if any
            if (videoElement.hls) {
                videoElement.hls.destroy();
            }
            
            const hls = new window.Hls();
            hls.loadSource(src);
            hls.attachMedia(videoElement);
            videoElement.hls = hls;
            videoElement.dataset.hlsConfigured = 'true';
        } else if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
            // Native HLS support (Safari)
            if (videoElement.src !== src) {
                videoElement.src = src;
                videoElement.dataset.hlsConfigured = 'true';
            }
        }
        
        // Force re-apply mobile configuration after HLS setup
        setTimeout(() => {
            this.configureVideoForMobile(videoElement);
        }, 100);
    }

    /**
     * Configure video element for mobile devices
     * Prevents fullscreen mode and sets appropriate attributes
     * @param {HTMLVideoElement} videoElement - The video element to configure
     */
    configureVideoForMobile(videoElement) {
        // Set attributes to prevent fullscreen on mobile
        videoElement.setAttribute('webkit-playsinline', 'true');
        videoElement.setAttribute('x5-playsinline', 'true');
        videoElement.setAttribute('x5-video-player-type', 'h5');
        videoElement.setAttribute('x5-video-player-fullscreen', 'false');
        videoElement.setAttribute('x5-video-orientation', 'portraint');
        videoElement.setAttribute('playsinline', 'true');
        
        // Force remove controls on mobile
        videoElement.removeAttribute('controls');
        
        // Prevent default fullscreen behavior with multiple event listeners
        const preventFullscreen = (e) => {
            e.preventDefault();
            e.stopPropagation();
            return false;
        };
        
        // Add multiple event listeners to catch all fullscreen attempts
        videoElement.addEventListener('webkitbeginfullscreen', preventFullscreen, true);
        videoElement.addEventListener('webkitendfullscreen', preventFullscreen, true);
        videoElement.addEventListener('webkitfullscreenchange', preventFullscreen, true);
        videoElement.addEventListener('fullscreenchange', preventFullscreen, true);
        videoElement.addEventListener('webkitfullscreenchange', preventFullscreen, true);
        
        // Prevent click events that might trigger fullscreen
        videoElement.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
        }, true);
        
        // Set CSS properties to prevent fullscreen
        videoElement.style.webkitPresentationMode = 'inline';
        videoElement.style.webkitPlaysinline = 'true';
        
        // Force inline display
        videoElement.style.position = 'relative';
        videoElement.style.zIndex = '1';
    }

    /**
     * Setup intersection observer for preview videos
     */
    setupIntersectionObserver() {
        if (this._intersectionObserver) {
            this._intersectionObserver.disconnect();
        }

        this._intersectionObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const videoItem = entry.target;
                    const videoId = videoItem.dataset.videoId;
                    const video = this.videos.find(v => v.id === videoId);
                    if (video) {
                        // Gestion du previewAutoplay (indépendant de previewMode)
                        // previewMode = dimensions plus grandes + lecture inline au clic
                        // previewAutoplay = contrôle l'autoplay des vidéos de preview
                        
                        if (this.previewAutoplay === 'none') {
                            // Ne charge que le poster, mais configure le hover si previewMode actif
                            this.loadPosterImage(videoItem, video);
                        } else if (this.previewAutoplay === 'first') {
                            // Charge la preview seulement pour le premier item
                            const allItems = Array.from(this.shadowRoot.querySelectorAll('.video-item'));
                            const firstItem = allItems[0];
                            if (videoItem === firstItem) {
                                this.loadPreviewVideo(videoItem, video);
                            } else {
                                this.loadPosterImage(videoItem, video);
                            }
                        } else { // 'all' (défaut)
                            // loadPreviewVideo gère le mode preview en configurant le hover
                            this.loadPreviewVideo(videoItem, video);
                        }
                    }
                }
            });
        }, {
            rootMargin: '50px',
            threshold: 0.1
        });

        // Observe all video items that haven't been observed yet
        const videoItems = this.shadowRoot.querySelectorAll('.video-item');
        videoItems.forEach(item => {
            if (item.dataset.intersectionObserved !== 'true') {
                this._intersectionObserver.observe(item);
                item.dataset.intersectionObserved = 'true';
            }
        });
    }

    /**
     * Load preview video for a video item
     * @param {HTMLElement} videoItem - The video item element
     * @param {Object} video - The video data object
     */
    async loadPreviewVideo(videoItem, video) {
        // Check if this video item has already been processed
        if (videoItem.dataset.previewLoaded === 'true') {
            return; // Already loaded, skip
        }

        const placeholder = videoItem.querySelector('.video-placeholder');
        let previewVideo = videoItem.querySelector('.video-preview');
        let posterImg = videoItem.querySelector('.poster-img');
        
        // Check user's motion preference
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        // Check connection quality
        const isGoodConnection = await this.checkConnectionQuality();
        
        // Always load poster image if not already loaded
        if (video.poster && !posterImg) {
            posterImg = document.createElement('img');
            posterImg.className = 'poster-img poster-img-absolute';
            posterImg.src = video.poster;
            // Hide placeholder when image is loaded
            posterImg.addEventListener('load', function() {
                if (placeholder) placeholder.classList.add('loaded');
            });
            placeholder.appendChild(posterImg);
        }
        
        // Only create video preview if video.preview exists AND user doesn't prefer reduced motion AND connection is good
        if (video.preview && !previewVideo && !prefersReducedMotion && isGoodConnection) {
            // Create preview video for autoplay
            previewVideo = document.createElement('video');
            previewVideo.className = 'video-preview';
            previewVideo.muted = true;
            previewVideo.loop = true;
            previewVideo.playsInline = true;
            previewVideo.poster = video.poster;
            // CSS handles pointer-events: none via .video-preview class
            placeholder.appendChild(previewVideo);

            // Load video source immediately to start buffering
            if (previewVideo.src !== video.preview) {
                previewVideo.src = video.preview;
                previewVideo.onloadeddata = () => {
                    previewVideo.classList.add('loaded');
                    if (placeholder) placeholder.classList.add('loaded');
                };
            }

            // Setup autoplay: play when visible, pause when not visible
            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        // Video is visible, start playing
                        previewVideo.play().catch(e => console.log('Preview play failed:', e));
                    } else {
                        previewVideo.pause();
                    }
                });
            }, {
                rootMargin: '50px',
                threshold: 0.1
            });

            observer.observe(videoItem);
            videoItem.dataset.observed = 'true';
            
            // If previewMode is enabled, also setup click handler for inline playback
            if (this.previewMode) {
                this.setupPreviewModeClick(videoItem);
            }
        } else if (video.preview && previewVideo && !prefersReducedMotion) {
            // Video element already exists (from previous load)
            const isObserved = videoItem.dataset.observed === 'true';
            if (!isObserved) {
                // Create new observer only if not already observed
                const observer = new IntersectionObserver((entries) => {
                    entries.forEach(entry => {
                        if (entry.isIntersecting) {
                            // Video is visible, start playing
                            previewVideo.play().catch(e => console.log('Preview play failed:', e));
                        } else {
                            previewVideo.pause();
                        }
                    });
                }, {
                    rootMargin: '50px',
                    threshold: 0.1
                });

                observer.observe(videoItem);
                videoItem.dataset.observed = 'true';
            }
            
            // If previewMode is enabled, also setup click handler for inline playback
            if (this.previewMode && videoItem.dataset.previewModeEnabled !== 'true') {
                this.setupPreviewModeClick(videoItem);
            }
        }
        
        // If previewMode is enabled but no preview video, still setup click handler
        if (this.previewMode && videoItem.dataset.previewModeEnabled !== 'true') {
            this.setupPreviewModeClick(videoItem);
        }
        
        // If user prefers reduced motion, ensure poster image is visible and video is hidden
        if (prefersReducedMotion && previewVideo) {
            previewVideo.classList.add('hidden');
            if (posterImg) {
                posterImg.classList.remove('hidden');
            }
        }

        // Mark this video item as processed to prevent duplicate loading
        if (!this.previewMode) {
            videoItem.dataset.previewLoaded = 'true';
        }
    }

    /**
     * Load only poster image for a video item (used in hover mode)
     * @param {HTMLElement} videoItem - The video item element
     * @param {Object} video - The video data object
     */
    loadPosterImage(videoItem, video) {
        // Check if this video item has already been processed
        if (videoItem.dataset.posterLoaded === 'true') {
            return; // Already loaded, skip
        }

        const placeholder = videoItem.querySelector('.video-placeholder');
        let posterImg = videoItem.querySelector('.poster-img');
        
        // Load poster image if not already loaded
        if (video.poster && !posterImg) {
            posterImg = document.createElement('img');
            posterImg.className = 'poster-img poster-img-absolute';
            posterImg.src = video.poster;
            // Hide placeholder when image is loaded
            posterImg.addEventListener('load', function() {
                if (placeholder) placeholder.classList.add('loaded');
            });
            placeholder.appendChild(posterImg);
        }

        // Mark this video item as processed to prevent duplicate loading
        videoItem.dataset.posterLoaded = 'true';
        
        // In preview mode, setup click handler for inline playback
        if (this.previewMode) {
            this.setupPreviewModeClick(videoItem);
        }
    }

    /**
     * Load preview video on first hover (used in hover mode)
     * @param {HTMLElement} videoItem - The video item element
     * @param {Object} video - The video data object
     */
    async loadPreviewVideoOnHover(videoItem, video) {
        // Check if this video item has already been processed
        if (videoItem.dataset.previewLoaded === 'true') {
            return; // Already loaded, skip
        }

        const placeholder = videoItem.querySelector('.video-placeholder');
        let previewVideo = videoItem.querySelector('.video-preview');
        let posterImg = videoItem.querySelector('.poster-img');
        
        // Check user's motion preference
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        // Check connection quality
        const isGoodConnection = await this.checkConnectionQuality();
        
        // Only create video preview if video.preview exists AND user doesn't prefer reduced motion AND connection is good
        if (video.preview && !previewVideo && !prefersReducedMotion && isGoodConnection) {
            previewVideo = document.createElement('video');
            previewVideo.className = 'video-preview';
            previewVideo.muted = true;
            previewVideo.loop = true;
            previewVideo.playsInline = true;
            // CSS handles pointer-events: none via .video-preview class
            placeholder.appendChild(previewVideo);

            // Quand la preview est chargée, masquer le poster (sans le recréer ni le supprimer)
            previewVideo.onloadeddata = () => {
                previewVideo.classList.add('loaded');
                if (posterImg) posterImg.classList.add('hidden');
                if (placeholder) placeholder.classList.add('loaded');
            };

            previewVideo.src = video.preview;
        }
        // Mark this video item as processed to prevent duplicate loading
        videoItem.dataset.previewLoaded = 'true';
    }

    /**
     * Fetch videos from JSON URL
     * @returns {Promise<Array>} Promise that resolves to videos array
     */
    async fetchVideosFromJSON() {
        if (!this.jsonUrl) {
            return [];
        }

        try {
            const response = await fetch(this.jsonUrl);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            
            // Transform JSON data to internal format
            return Array.isArray(data) ? data.map(video => ({
                id: video.id || video.videoUID,
                title: video.title || video.videoTitle,
                src: video.src || video.videoUrl,
                poster: video.poster || video.previewImgUrl,
                preview: video.preview || video.previewVideoUrl,
                format: video.format || video.videoFormat,
                cta: video.cta || []
            })) : [];
        } catch (error) {
            console.error('Error fetching videos from JSON:', error);
            return [];
        }
    }

    /**
     * Set up event listeners based on component mode
     */
    setupEventListeners() {
        if (this.isWidgetMode()) {
            this.setupWidgetEvents();
        } else {
            this.setupCarouselEvents();
        }
    }

    /**
     * Set up event listeners for widget mode
     * Handles mini widget clicks, dialog close, and keyboard events
     */
    setupWidgetEvents() {
        // Mini widget click and keyboard handlers
        const mini = this.shadowRoot.querySelector('.mini');
        const miniCloseBtn = this.shadowRoot.querySelector('.mini-close-btn');
        
        if (mini) {
            mini.onclick = async (e) => {
                // Don't open dialog if clicking the close button
                if (e.target.closest('.mini-close-btn')) {
                    return;
                }
                await this.loadHLS();
                await this.openDialog();
                e.stopPropagation();
            };
            
            // Keyboard navigation for widget (only when visible/focusable)
            mini.addEventListener('keydown', async (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    // Don't trigger if focused on close button
                    if (e.target.closest('.mini-close-btn')) {
                        return;
                    }
                    e.preventDefault();
                    await this.loadHLS();
                    await this.openDialog();
                }
            });
            
            // Ensure widget is focusable when it becomes visible again
            mini.addEventListener('focus', () => {
                if (mini.tabIndex === -1) {
                    mini.tabIndex = 0;
                }
            });
        }
        
        // Mini close button handler
        if (miniCloseBtn) {
            miniCloseBtn.onclick = (e) => {
                e.stopPropagation();
                this.closeWidget();
            };
            
            miniCloseBtn.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    this.closeWidget();
                }
            });
        }
        
        // Dialog close handlers
        const dialog = this.shadowRoot.querySelector('.modal-dialog.widget-mode');
        const closeBtn = this.shadowRoot.querySelector('.close-button');
        if (closeBtn) {
            // Make close button focusable
            closeBtn.tabIndex = 0;
            closeBtn.setAttribute('role', 'button');
            closeBtn.setAttribute('aria-label', this.getLabel('close'));
            closeBtn.title = this.getLabel('close');
            
            closeBtn.onclick = async (e) => {
                await this.closeDialog();
                e.stopPropagation();
            };
            
            // Keyboard navigation for close button
            closeBtn.addEventListener('keydown', async (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    await this.closeDialog();
                }
            });
        }
        
        // Pause button handler for widget modal
        const pauseBtn = this.shadowRoot.querySelector('.modal-dialog.widget-mode .pause-button');
        const modalVideo = this.shadowRoot.querySelector('.modal-dialog.widget-mode .modal-video-main');
        if (pauseBtn && modalVideo) {
            pauseBtn.onclick = (e) => {
                e.stopPropagation();
                if (modalVideo.paused) {
                    modalVideo.play();
                } else {
                    modalVideo.pause();
                }
                // Update button state after play/pause action
                this.updatePauseButton(pauseBtn, modalVideo.paused);
            };
        }
        
        // Mute button handler for widget modal
        const muteBtn = this.shadowRoot.querySelector('.modal-dialog.widget-mode .mute-button');
        if (muteBtn && modalVideo) {
            // Set initial mute state
            modalVideo.muted = this._globalMutedState;
            this.updateMuteButton(muteBtn, modalVideo.muted);
            
            muteBtn.onclick = (e) => {
                e.stopPropagation();
                modalVideo.muted = !modalVideo.muted;
                this._globalMutedState = modalVideo.muted;
                this.updateMuteButton(muteBtn, modalVideo.muted);
            };
        }
        
        if (dialog) {
                    dialog.addEventListener('click', async (e) => {
             if (e.target === dialog) {
                 await this.closeDialog();
             }
         });
        }
        
        // Escape key handler
        document.removeEventListener('keydown', this._widgetKeyHandler);
        this._widgetKeyHandler = async (e) => {
            if (this._modalWidgetActive && e.key === 'Escape') {
                await this.closeDialog();
            }
        };
        document.addEventListener('keydown', this._widgetKeyHandler);
    }

    /**
     * Set up event listeners for carousel mode
     * Handles video item clicks, navigation, and keyboard events
     */
    setupCarouselEvents() {
        // Setup carousel navigation buttons
        this.setupCarouselNavigation();
        // Handle video item clicks to open dialog - use shadowRoot instead of this
        this.shadowRoot.addEventListener('click', async (e) => {
            const videoItem = e.target.closest('.video-item');
            if (videoItem) {
                // In preview mode, let the individual click handler handle it
                if (this.previewMode) {
                    e.preventDefault();
                    e.stopPropagation();
                    return; // Skip dialog opening in hover mode
                }
                
                const videoId = videoItem.dataset.videoId;
                const videoIndex = this.videos.findIndex(video => video.id === videoId);
                if (videoIndex !== -1) {
                    this.currentVideoIndex = videoIndex;
                    this.dispatchVideoChanged();
                    await this.loadHLS();
                    this.openDialog();
                }
            }
        });

        // Progress bar click handler - works for both carousel and widget modals
        this.shadowRoot.addEventListener('click', (e) => {
            const progressBar = e.target.closest('.progress-bar');
            if (progressBar) {
                // Find the video in the same wrapper as the progress bar
                const wrapper = progressBar.closest('.video-wrapper');
                const video = wrapper ? wrapper.querySelector('.modal-video-main') : null;
                if (video && !isNaN(video.duration)) {
                    const rect = progressBar.getBoundingClientRect();
                    const clickX = e.clientX - rect.left;
                    const percentage = clickX / rect.width;
                    const newTime = percentage * video.duration;
                    video.currentTime = newTime;
                }
            }
        });
        
        // Navigation and close buttons in modal
        this.shadowRoot.addEventListener('click', (e) => {
            if (e.target.classList.contains('close-button')) {
                this.closeDialog();
            }
        });
        
        // Setup swipe functionality for modal dialog
        this.setupModalSwipe();
        
        // Keyboard navigation for modal
        document.removeEventListener('keydown', this._carouselKeyHandler);
        this._carouselKeyHandler = (e) => {
            if (e.key === 'Escape') {
                this.closeDialog();
            }
            const modalDialog = this.shadowRoot.querySelector('.modal-dialog');
            if (modalDialog && modalDialog.open) {
                if (this.modalDirection === 'horizontal') {
                    if (e.key === 'ArrowLeft') this.previousVideo();
                    if (e.key === 'ArrowRight') this.nextVideo();
                } else {
                    if (e.key === 'ArrowUp') this.previousVideo();
                    if (e.key === 'ArrowDown') this.nextVideo();
                }
            }
        };
        document.addEventListener('keydown', this._carouselKeyHandler);

        // Keyboard navigation for video items outside dialog
        this.shadowRoot.addEventListener('keydown', async (e) => {
            const focusedVideoItem = e.target.closest('.video-item');
            if (!focusedVideoItem) return;

            const allVideoItems = Array.from(this.shadowRoot.querySelectorAll('.video-item'));
            const currentIndex = allVideoItems.indexOf(focusedVideoItem);

            if (this.shape === 'horizontal' || this.shape === 'bubble') {
                // Horizontal navigation
                if (e.key === 'ArrowLeft' && currentIndex > 0) {
                    e.preventDefault();
                    allVideoItems[currentIndex - 1].focus();
                } else if (e.key === 'ArrowRight' && currentIndex < allVideoItems.length - 1) {
                    e.preventDefault();
                    allVideoItems[currentIndex + 1].focus();
                }
            } else if (this.shape === 'vertical') {
                // Vertical navigation
                if (e.key === 'ArrowUp' && currentIndex > 0) {
                    e.preventDefault();
                    allVideoItems[currentIndex - 1].focus();
                } else if (e.key === 'ArrowDown' && currentIndex < allVideoItems.length - 1) {
                    e.preventDefault();
                    allVideoItems[currentIndex + 1].focus();
                }
            }

            // Open dialog with Enter or Space
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (this.previewMode) {
                    // Simuler le click handler preview-mode
                    if (focusedVideoItem._hoverClickHandler) {
                        focusedVideoItem._hoverClickHandler(e);
                        // Donner le focus au bouton play/pause si présent
                        setTimeout(() => {
                            const playPauseBtn = focusedVideoItem.querySelector('.video-play-pause-btn');
                            if (playPauseBtn) playPauseBtn.focus();
                        }, 0);
                    }
                    return;
                }
                const videoId = focusedVideoItem.dataset.videoId;
                const videoIndex = this.videos.findIndex(video => video.id === videoId);
                if (videoIndex !== -1) {
                    this.currentVideoIndex = videoIndex;
                    this.dispatchVideoChanged();
                    await this.loadHLS();
                    this.openDialog();
                }
            }
        });
        
        // Update pause button icon for current carousel video
        this.updatePauseButtonIcon = () => {
            const currentVideo = this.shadowRoot.querySelector(`.modal-video-main[data-video-index="${this.currentVideoIndex}"]`);
            if (currentVideo) {
                const wrapper = currentVideo.closest('.video-wrapper');
                if (wrapper) {
                    const pauseBtn = wrapper.querySelector('.pause-button');
                    this.updatePauseButton(pauseBtn, currentVideo.paused);
                }
            }
        };

        // Update mute button icon for current carousel video
        this.updateMuteButtonIcon = () => {
            const currentVideo = this.shadowRoot.querySelector(`.modal-video-main[data-video-index="${this.currentVideoIndex}"]`);
            if (currentVideo) {
                const wrapper = currentVideo.closest('.video-wrapper');
                if (wrapper) {
                    const muteBtn = wrapper.querySelector('.mute-button');
                    this.updateMuteButton(muteBtn, currentVideo.muted);
                }
            }
        };
        
        this.updateModalNavButtons();
    }

    /**
     * Setup swipe functionality for modal dialog
     * Handles touch events to detect swipe gestures for video navigation
     */
    setupModalSwipe() {
        const modalDialog = this.shadowRoot.querySelector('.modal-dialog');
        if (!modalDialog) return;

        let startX = 0;
        let startY = 0;
        let endX = 0;
        let endY = 0;
        let isSwiping = false;
        let swipeStartTime = 0;
        const minSwipeDistance = 50;
        const maxSwipeTime = 500;

        // Touch start event
        const handleTouchStart = (e) => {
            if (e.touches.length === 1) {
                startX = e.touches[0].clientX;
                startY = e.touches[0].clientY;
                swipeStartTime = Date.now();
                isSwiping = false;
            }
        };

        // Touch move event
        const handleTouchMove = (e) => {
            if (e.touches.length === 1) {
                endX = e.touches[0].clientX;
                endY = e.touches[0].clientY;
                
                const deltaX = Math.abs(endX - startX);
                const deltaY = Math.abs(endY - startY);
                
                // Determine if this is a horizontal or vertical swipe
                if (deltaX > deltaY && deltaX > minSwipeDistance) {
                    isSwiping = true;
                    e.preventDefault(); // Prevent default scrolling during swipe
                } else if (deltaY > deltaX && deltaY > minSwipeDistance) {
                    isSwiping = true;
                    e.preventDefault(); // Prevent default scrolling during swipe
                }
            }
        };

        // Touch end event
        const handleTouchEnd = (e) => {
            if (isSwiping) {
                const deltaX = endX - startX;
                const deltaY = endY - startY;
                const swipeTime = Date.now() - swipeStartTime;
                
                // Only process swipe if it's fast enough (not a slow drag)
                if (swipeTime <= maxSwipeTime) {
                    // Determine swipe direction based on modal direction
                    if (this.modalDirection === 'horizontal') {
                        // For horizontal modal direction, use horizontal swipes
                        if (Math.abs(deltaX) > minSwipeDistance) {
                            if (deltaX > 0) {
                                // Swipe right - go to previous video
                                this.previousVideo();
                            } else {
                                // Swipe left - go to next video
                                this.nextVideo();
                            }
                        }
                    } else {
                        // For vertical modal direction, use vertical swipes
                        if (Math.abs(deltaY) > minSwipeDistance) {
                            if (deltaY > 0) {
                                // Swipe down - go to previous video
                                this.previousVideo();
                            } else {
                                // Swipe up - go to next video
                                this.nextVideo();
                            }
                        }
                    }
                }
                
                isSwiping = false;
            }
        };

        // Add touch event listeners to modal dialog
        modalDialog.addEventListener('touchstart', handleTouchStart, { passive: false });
        modalDialog.addEventListener('touchmove', handleTouchMove, { passive: false });
        modalDialog.addEventListener('touchend', handleTouchEnd, { passive: false });

        // Store references for cleanup
        this._modalSwipeHandlers = {
            handleTouchStart,
            handleTouchMove,
            handleTouchEnd
        };
    }

    /**
     * Update dialog content with current video information
     * Updates title, counter, and navigation buttons
     */
    updateDialogContent() {
        const currentVideo = this.videos[this.currentVideoIndex];
        const title = this.shadowRoot.querySelector('.modal-title');
        if (title) {
            title.textContent = currentVideo.title;
        }
        this.updateCounter();
        this.updateModalNavButtons();
    }

    /**
     * Update the video counter display
     * Shows current position
     */
    updateCounter() {
        const counter = this.shadowRoot.querySelector('.video-counter');
        if (counter) {
            counter.textContent = `${this.currentVideoIndex + 1} / ${this.videos.length}`;
        }
    }

    /**
     * Update navigation button states
     * Hides prev/next buttons when there's no previous/next video
     */
    updateModalNavButtons() {
        const currentVideo = this.shadowRoot.querySelector(`.modal-video-main[data-video-index="${this.currentVideoIndex}"]`);
        if (currentVideo) {
            const wrapper = currentVideo.closest('.video-wrapper');
            if (wrapper) {
                const prevBtn = wrapper.querySelector('.prev-button');
                const nextBtn = wrapper.querySelector('.next-button');
        
        if (prevBtn) {
            const hasPrevious = this.currentVideoIndex > 0;
            prevBtn.classList.toggle('hidden', !hasPrevious);
        }
        
        if (nextBtn) {
            const hasNext = this.currentVideoIndex < this.videos.length - 1;
            nextBtn.classList.toggle('hidden', !hasNext);
        }
        
        // Update button icons based on modal direction
        this.updateNavigationButtonIcons();
            }
        }
    }

    /**
     * Update progress bar
     * @param {HTMLVideoElement} video - The video element to track
     */
    updateProgressBar(video) {
        // Trouver le wrapper de la vidéo actuelle
        const wrapper = video.closest('.video-wrapper');
        if (!wrapper) return;

        const progressFill = wrapper.querySelector('.progress-fill');

        if (!progressFill) return;

        if (!isNaN(video.duration) && video.duration > 0) {
            const progress = (video.currentTime / video.duration) * 100;
            progressFill.style.width = `${progress}%`;
        }
    }

    /**
     * Toggle global mute state for all videos in the component
     */
    toggleGlobalMute() {
        this._globalMutedState = !this._globalMutedState;
        this.applyGlobalMuteState();
    }

    /**
     * Apply global mute state to all videos in the component
     */
    applyGlobalMuteState() {
        const allModalVideos = this.shadowRoot.querySelectorAll('.modal-video-main');
        allModalVideos.forEach(video => {
            video.muted = this._globalMutedState;
        });
        // Update all mute button icons
        this.updateAllMuteButtonIcons();
    }

    /**
     * Update all mute button icons based on global muted state
     */
    updateAllMuteButtonIcons() {
        const allMuteButtons = this.shadowRoot.querySelectorAll('.mute-button');
        allMuteButtons.forEach(muteBtn => {
            this.updateMuteButton(muteBtn, this._globalMutedState);
        });
    }

    /**
     * Open the modal dialog
     * Handles both widget and carousel modes
     */
    async openDialog() {
        if (this.isWidgetMode()) {
            this._modalWidgetActive = true;
            
            // Load modal video content when dialog opens (only once)
            const modalVideo = this.shadowRoot.querySelector('.modal-dialog.widget-mode .modal-video-main');
            
            if (modalVideo) {
                // Only load video if not already loaded
                if (!modalVideo.src) {
                    // Get video source
                    let src = this.getAttribute('src') || '';
                    let poster = this.getAttribute('poster') || '';
                    
                    // If we have JSON data, use the first video
                    if (this.videos && this.videos.length > 0) {
                        const firstVideo = this.videos[0];
                        src = firstVideo.src || src;
                        poster = firstVideo.poster || poster;
                    }
                    
                    if (src) {
                        modalVideo.classList.remove('hidden');
                        modalVideo.src = src;
                        if (poster) modalVideo.poster = poster;
                        
                        // Setup HLS if needed
                        await this.loadHLS();
                        this.setupHLSPlayer(modalVideo, src);
                        
                        // Force mobile configuration for widget video
                        this.configureVideoForMobile(modalVideo);
                        
                        // Add autoplay and playsinline attributes
                        modalVideo.autoplay = true;
                        modalVideo.playsInline = true;
                        modalVideo.muted = this._globalMutedState;
                        
                        // Setup progress tracking for widget video
                        this.setupProgressTracking(modalVideo);
                        
                        // Ensure video is visible after a short delay
                        setTimeout(() => {
                            modalVideo.classList.remove('hidden');
                            modalVideo.classList.add('video-ready');
                        }, 100);
                    } else {
                        console.log('No video source found for widget modal');
                    }
                } else {
                    // Video already loaded - restart it
                    modalVideo.classList.remove('hidden');
                    modalVideo.currentTime = 0;
                    modalVideo.muted = this._globalMutedState;
                    
                    // Setup progress tracking for widget video
                    this.setupProgressTracking(modalVideo);
                    
                    modalVideo.play().catch(() => {});
                }
            }
            
            // Pause the mini video when opening modal
            const miniVideo = this.shadowRoot.querySelector('.mini-video');
            if (miniVideo) {
                miniVideo.pause();
            }
            
            // Open the dialog
            const dialog = this.shadowRoot.querySelector('.modal-dialog.widget-mode');
            if (dialog) {
                dialog.showModal();
            }
            
            // Hide the mini widget while dialog is open
            const mini = this.shadowRoot.querySelector('.mini');
            if (mini) {
                mini.classList.remove('show');
            }
            
            // Ensure modal video starts playing
            if (modalVideo) {
                modalVideo.play().catch(() => {});
            }
            
            await this.updateWidgetContent();
            
            // Disable page scroll for widget mode too
            document.body.style.overflow = 'hidden';
            // Prevent scroll on mobile - preserve current scroll position
            const scrollY = window.scrollY;
            document.body.style.position = 'fixed';
            document.body.style.width = '100%';
            document.body.style.top = `-${scrollY}px`;
            
            // Focus the close button when dialog opens
            setTimeout(() => {
                const closeBtn = this.shadowRoot.querySelector('.close-button');
                if (closeBtn) {
                    closeBtn.focus();
                }
            }, 100);
        } else {
            const dialog = this.shadowRoot.querySelector('.modal-dialog');
            dialog.showModal();
            this.generateModalVideos();
            this.updateDialogContent();
            
            this.updateModalNavButtons();
            // Update navigation button icons based on modal direction
            this.updateNavigationButtonIcons();
            // Disable page scroll
            document.body.style.overflow = 'hidden';
            // Prevent scroll on mobile - preserve current scroll position
            const scrollY = window.scrollY;
            document.body.style.position = 'fixed';
            document.body.style.width = '100%';
            document.body.style.top = `-${scrollY}px`;
            if (!this._modalDialogClickHandler) {
                this._modalDialogClickHandler = (e) => {
                    const dialog = e.currentTarget;
                    // Empêcher la fermeture si clic sur un bouton ou sur la vidéo principale
                    const isButton = e.target.closest('button, [role="button"]');
                    const isVideo = e.target.classList && e.target.classList.contains('modal-video-main');
                    if (isButton || isVideo) {
                        return;
                    }
                    const video = dialog.querySelector('.modal-video-main');
                    if (video && !video.contains(e.target)) {
                        this.closeDialog();
                    }
                };
                dialog.addEventListener('click', this._modalDialogClickHandler);
            }
        }
    }

    /**
     * Setup HLS for the current video in modal
     */
    setupCurrentVideoHLS() {
        const currentVideo = this.videos[this.currentVideoIndex];
        if (currentVideo && currentVideo.src) {
            const modalVideo = this.shadowRoot.querySelector('.modal-video-main');
            if (modalVideo) {
                this.setupHLSPlayer(modalVideo, currentVideo.src);
                modalVideo.poster = currentVideo.poster;
            }
        }
    }
    
    /**
     * Close the modal dialog
     * Handles both widget and carousel modes
     */
    async closeDialog() {
        if (this.isWidgetMode()) {
            this._modalWidgetActive = false;
            
            // Close the dialog
            const dialog = this.shadowRoot.querySelector('.modal-dialog.widget-mode');
            if (dialog) {
                dialog.close();
            }
            
            // Reset modal video
            const modalVideo = this.shadowRoot.querySelector('.modal-dialog.widget-mode .modal-video-main');
            if (modalVideo) {
                modalVideo.pause();
                modalVideo.currentTime = 0;
            }
            
            // Reset pause button state (show pause icon since video will autoplay when reopened)
            const pauseBtn = this.shadowRoot.querySelector('.modal-dialog.widget-mode .pause-button');
            this.updatePauseButton(pauseBtn, false);
            
            // Show the mini widget again with transition
            const mini = this.shadowRoot.querySelector('.mini');
            if (mini) {
                mini.classList.add('show');
            }
            
            // Restart the mini video preview
            const miniVideo = this.shadowRoot.querySelector('.mini-video');
            const miniPlaceholder = this.shadowRoot.querySelector('.mini .placeholder');
            if (miniVideo) {
                // Show video, hide placeholder via CSS classes
                miniVideo.classList.remove('hidden');
                if (miniPlaceholder) {
                    miniPlaceholder.classList.add('hidden');
                }
                // Reset and play the video only if it has a source
                if (miniVideo.src) {
                    miniVideo.currentTime = 0;
                    miniVideo.muted = true; // Mini video should be muted
                    miniVideo.loop = true;
                    miniVideo.play().catch((e) => {
                        console.log('Could not play mini video:', e);
                    });
                }
            }
            
            // Re-enable page scroll for widget mode
            document.body.style.overflow = '';
            // Re-enable scroll on mobile - restore scroll position
            const scrollY = document.body.style.top;
            document.body.style.position = '';
            document.body.style.width = '';
            document.body.style.top = '';
            // Restore scroll position
            if (scrollY) {
                const scrollYValue = parseInt(scrollY.replace('px', '').replace('-', ''));
                window.scrollTo(0, scrollYValue);
            }
            
            // Cleanup scroll detection since widget is now hidden
            this.cleanupScrollDetection();
        } else {
            const dialog = this.shadowRoot.querySelector('.modal-dialog');
            dialog.close();
            // Remove focus from the video bubble if it has it
            const activeElement = this.shadowRoot.activeElement;
            if (activeElement && activeElement.classList && activeElement.classList.contains('video-item')) {
                activeElement.blur();
            }
            // Stop all videos in the modal
            const modalVideos = this.shadowRoot.querySelectorAll('.modal-video-main');
            modalVideos.forEach(video => {
                video.pause();
                video.currentTime = 0;
            });
            
            // Re-enable page scroll
            document.body.style.overflow = '';
            // Re-enable scroll on mobile - restore scroll position
            const scrollY = document.body.style.top;
            document.body.style.position = '';
            document.body.style.width = '';
            document.body.style.top = '';
            // Restore scroll position
            if (scrollY) {
                const scrollYValue = parseInt(scrollY.replace('px', '').replace('-', ''));
                window.scrollTo(0, scrollYValue);
            }
        }
        if (!this.isWidgetMode()) {
            const dialog = this.shadowRoot.querySelector('.modal-dialog');
            if (dialog && this._modalDialogClickHandler) {
                dialog.removeEventListener('click', this._modalDialogClickHandler);
                this._modalDialogClickHandler = null;
            }
        }

        const closedEv = new CustomEvent(ROCKETSTORIES_EVENTS.VIDEO_MODAL_CLOSED, {
            bubbles: true,
            composed: true,
            detail: { source: this.isWidgetMode() ? 'widget' : 'carousel' }
        });
        this.dispatchEvent(closedEv);
    }

    /**
     * Collect videos from DOM or data attribute
     * Supports both static video items and dynamic generation from data-videos attribute
     */
    async collectVideos() {
        // 0. Check if <video-item> children are present in the light DOM
        const videoItemElements = Array.from(this.querySelectorAll('video-item'));
        if (videoItemElements.length > 0) {
            // Use data from <video-item> as the main source
            this.videos = videoItemElements
                .map(item => item.videoData)
                .filter(data => !!data && data.id); // Only keep valid objects with an id
            if (this.videos.length > 0) {
                if (this.isWidgetMode()) {
                    this.updateWidgetContent();
                } else {
                    this.generateVideoItems();
                    this.applyShape();
                    this.setupIntersectionObserver();
                }
            }
        } else {
            // 1. Check if the videos-inline attribute is present
            const inlineJson = this.getAttribute('videos-inline');
            if (inlineJson) {
                try {
                    const data = JSON.parse(inlineJson);
                    this.videos = Array.isArray(data) ? data.map(video => ({
                        id: video.id || video.videoUID,
                        title: video.title || video.videoTitle,
                        src: video.src || video.videoUrl,
                        poster: video.poster || video.previewImgUrl,
                        preview: video.preview || video.previewVideoUrl,
                        format: video.format || video.videoFormat,
                        cta: video.cta || []
                    })) : [];
                } catch (e) {
                    console.error('Error parsing videos-inline:', e);
                    this.videos = [];
                }
                if (this.videos.length > 0) {
                    if (this.isWidgetMode()) {
                        this.updateWidgetContent();
                    } else {
                        this.generateVideoItems();
                        this.applyShape();
                        this.setupIntersectionObserver();
                    }
                }
            } else if (this.jsonUrl) {
                // Check if videos are defined via JSON URL
                this.videos = await this.fetchVideosFromJSON();
                if (this.videos.length > 0) {
                    if (this.isWidgetMode()) {
                        // For widget mode, use the first video
                        this.updateWidgetContent();
                    } else {
                        this.generateVideoItems();
                        this.applyShape();
                        this.setupIntersectionObserver();
                    }
                }
            } else {
                // Check if videos are defined via data-videos attribute
                const dataVideos = this.getAttribute('data-videos');
                if (dataVideos) {
                    // Generate videos from data-videos attribute
                    const videoIds = dataVideos.split(',').map(id => id.trim());
                    this.videos = videoIds.map(id => ({
                        id: id,
                        title: `Video ${id}`
                    }));
                    
                    if (this.isWidgetMode()) {
                        // For widget mode, use the first video
                        this.updateWidgetContent();
                    } else {
                        // Generate video items in Shadow DOM using template
                        this.generateVideoItems();
                        // Apply shape after videos are generated
                        this.applyShape();
                    }
                } else {
                    // Fallback to existing video items in Shadow DOM
                    const videoItems = this.shadowRoot.querySelectorAll('.video-item');
                    if (videoItems.length > 0) {
                        // Make all existing video items focusable
                        videoItems.forEach(item => {
                            item.tabIndex = 0;
                        });
                        
                        this.videos = Array.from(videoItems).map(item => ({
                            id: item.dataset.videoId,
                            title: item.dataset.videoTitle
                        }));
                        // Apply shape to existing videos
                        this.applyShape();
                    } else {
                        // No videos found, create empty array
                        this.videos = [];
                    }
                }
            }
        }
        this.updateCounter();
        return Promise.resolve();
    }

    /**
     * Generate video items from template in Shadow DOM
     * Creates interactive video elements with proper data attributes
     */
    generateVideoItems() {
        const template = document.getElementById('video-item-template');
        if (!template) {
            console.error('Template video-item-template not found');
            return;
        }

        // Clear existing content in Shadow DOM
        const carouselContainer = this.shadowRoot.querySelector('.carousel-container');
        if (carouselContainer) {
            carouselContainer.innerHTML = '';
        }

        // Generate video items from template in Shadow DOM
        this.videos.forEach(video => {
            const videoItem = template.content.cloneNode(true);
            const videoDiv = videoItem.querySelector('.video-item');
            const placeholder = videoItem.querySelector('.video-placeholder');
            
            videoDiv.dataset.videoId = video.id;
            videoDiv.dataset.videoTitle = video.title;
            videoDiv.tabIndex = 0; // Make element focusable
            
            // Video title below thumbnail
            const titleEl = videoItem.querySelector('.video-title');
            if (titleEl) titleEl.textContent = video.title || '';
            
            // Clear existing content - no title for preview videos
            placeholder.innerHTML = '';

            // Add play icon at center if not in preview mode
            if (!this.previewMode) {
                const playIcon = document.createElement('div');
                playIcon.className = 'bubble-play-icon';
                playIcon.innerHTML = `
                  <svg width="64" height="64"><use href="#icon-play"></use></svg>
                `;
                placeholder.appendChild(playIcon);
            }
            
            if (carouselContainer) {
                carouselContainer.appendChild(videoItem);
            } else {
                // Fallback: append to shadowRoot if carousel-container not found
                this.shadowRoot.appendChild(videoItem);
            }
        });

        // Update carousel navigation buttons after generating videos
        setTimeout(() => {
            this.updateCarouselNavigationButtons();
        }, 100);
    }

    /**
     * Generate modal video elements for navigation
     * Creates all video placeholders in the modal dialog
     */
    generateModalVideos() {
        const modalVideoContainer = this.shadowRoot.querySelector('.modal-video-container');
        if (!modalVideoContainer) return;
        
        // Vérifier s'il y a déjà des vidéos chargées
        const existingVideos = modalVideoContainer.querySelectorAll('.modal-video-main');
        const hasLoadedVideos = existingVideos.length > 0 && existingVideos[0].src;
        
        if (hasLoadedVideos) {
            // Les vidéos sont déjà chargées, on les réutilise
            this.showCurrentVideo();
            return;
        }
        
        // Supprimer les anciens éléments vidéo non chargés
        modalVideoContainer.innerHTML = '';
        
        // Créer tous les wrappers vidéo
        this.videos.forEach((video, index) => {
            // Create wrapper with active class for current video
            const wrapper = document.createElement('div');
            wrapper.className = 'video-wrapper';
            if (index === this.currentVideoIndex) {
                wrapper.classList.add('active');
            }

            // Progress bar
            const progressContainer = document.createElement('div');
            progressContainer.className = 'progress-container';
            const progressBar = document.createElement('div');
            progressBar.className = 'progress-bar';
            const progressFill = document.createElement('div');
            progressFill.className = 'progress-fill';
            progressBar.appendChild(progressFill);
            progressContainer.appendChild(progressBar);

            // Shimmer loading placeholder
            const loadingPlaceholder = document.createElement('div');
            loadingPlaceholder.className = 'video-loading-placeholder';

            // Video
            const videoElement = document.createElement('video');
            videoElement.className = 'modal-video-main';
            videoElement.controls = false;
            videoElement.poster = video.poster;
            videoElement.dataset.videoIndex = index;
            videoElement.classList.add('video-ready'); // CSS handles sizing
            videoElement.muted = this._globalMutedState; // Apply global mute state
            // Pas de src ici !

            // Configure video for mobile
            this.configureVideoForMobile(videoElement);
            
            // Événements pour mettre à jour l'icône du bouton pause
            videoElement.addEventListener('play', () => {
                if (this.updatePauseButtonIcon) {
                    this.updatePauseButtonIcon();
                }
            });
            videoElement.addEventListener('pause', () => {
                if (this.updatePauseButtonIcon) {
                    this.updatePauseButtonIcon();
                }
            });
            videoElement.addEventListener('volumechange', () => {
                // Update global muted state based on current video
                this._globalMutedState = videoElement.muted;
                // Update all mute button icons
                this.updateAllMuteButtonIcons();
            });
            // Ajout: passer à la vidéo suivante à la fin
            videoElement.addEventListener('ended', () => {
                if (this.currentVideoIndex < this.videos.length - 1) {
                    this.nextVideo();
                } else {
                    this.closeDialog();
                }
            });
            
            // Controls
            const controls = document.createElement('div');
            controls.className = 'controls';
            // Close
            const closeBtn = document.createElement('button');
            closeBtn.className = 'close-button';
            closeBtn.title = this.getLabel('close');
            closeBtn.setAttribute('aria-label', this.getLabel('close'));
            closeBtn.innerHTML = '<svg width="24" height="24"><use href="#icon-close"></use></svg>';
            // Ajouter le gestionnaire d'événement pour close
            closeBtn.onclick = () => {
                this.closeDialog();
            };
            controls.appendChild(closeBtn);
            // Pause
            const pauseBtn = document.createElement('button');
            pauseBtn.className = 'pause-button';
            pauseBtn.title = this.getLabel('pause');
            pauseBtn.setAttribute('aria-label', this.getLabel('pause'));
            pauseBtn.innerHTML = '<svg width="24" height="24"><use href="#icon-pause"></use></svg>';
            // Ajouter le gestionnaire d'événement pour pause
            pauseBtn.onclick = () => {
                if (videoElement.paused) {
                    videoElement.play();
                } else {
                    videoElement.pause();
                }
            };
            controls.appendChild(pauseBtn);
            // Mute
            const muteBtn = document.createElement('button');
            muteBtn.className = 'mute-button';
            muteBtn.title = this.getLabel('mute');
            muteBtn.setAttribute('aria-label', this.getLabel('mute'));
            muteBtn.innerHTML = '<svg width="24" height="24"><use href="#icon-unmute"></use></svg>';
            // Ajouter le gestionnaire d'événement pour mute
            muteBtn.onclick = () => {
                this.toggleGlobalMute();
            };
            controls.appendChild(muteBtn);
            // Prev
            const prevBtn = document.createElement('button');
            prevBtn.className = 'nav-button prev-button';
            prevBtn.title = this.getLabel('prev');
            prevBtn.setAttribute('aria-label', this.getLabel('prev'));
            prevBtn.innerHTML = '<svg width="24" height="24"><use href="#icon-left"></use></svg>';
            // Ajouter le gestionnaire d'événement pour prev
            prevBtn.onclick = () => {
                this.previousVideo();
            };
            controls.appendChild(prevBtn);
            // Next
            const nextBtn = document.createElement('button');
            nextBtn.className = 'nav-button next-button';
            nextBtn.title = this.getLabel('next');
            nextBtn.setAttribute('aria-label', this.getLabel('next'));
            nextBtn.innerHTML = '<svg width="24" height="24"><use href="#icon-right"></use></svg>';
            // Ajouter le gestionnaire d'événement pour next
            nextBtn.onclick = () => {
                this.nextVideo();
            };
            controls.appendChild(nextBtn);

            // Shimmer: hide when video loads
            const onVideoLoaded = () => {
                wrapper.classList.add('video-loaded');
            };
            videoElement.addEventListener('loadeddata', onVideoLoaded, { once: true });
            videoElement.addEventListener('canplay', onVideoLoaded, { once: true });

            // Ajoute tout dans le wrapper
            wrapper.appendChild(progressContainer);
            wrapper.appendChild(videoElement);
            wrapper.appendChild(loadingPlaceholder);
            wrapper.appendChild(controls);

            // CTA Button
            if (video.cta && video.cta.length > 0) {
                const ctaContainer = document.createElement('div');
                ctaContainer.className = 'cta-container';
                
                video.cta.forEach(ctaItem => {
                    if (ctaItem.label && ctaItem.link) {
                        const ctaLink = document.createElement('a');
                        ctaLink.className = 'cta-button';
                        ctaLink.href = ctaItem.link;
                        ctaLink.textContent = ctaItem.label;
                        ctaLink.target = '_blank';
                        ctaLink.rel = 'noopener noreferrer';
                        ctaContainer.appendChild(ctaLink);
                    }
                });
                
                if (ctaContainer.children.length > 0) {
                    wrapper.appendChild(ctaContainer);
                }
            }

            // Ajoute le wrapper au container
            modalVideoContainer.appendChild(wrapper);
        });
        this.showCurrentVideo();
    }

    /**
     * Show only the current video in modal
     * Hides all other videos for single-video display
     */
    showCurrentVideo() {
        const allWrappers = this.shadowRoot.querySelectorAll('.video-wrapper');
        allWrappers.forEach((wrapper) => {
            const video = wrapper.querySelector('.modal-video-main');
            const videoIndex = parseInt(video.dataset.videoIndex);
            const isCurrent = videoIndex === this.currentVideoIndex;
            wrapper.classList.toggle('active', isCurrent);
            
            if (isCurrent) {
                // Charger le src si pas déjà fait
                if (!video.src) {
                    wrapper.classList.remove('video-loaded'); // Show shimmer until loaded
                    const v = this.videos[videoIndex];
                    if (v && v.src) {
                        video.src = v.src;
                        this.setupHLSPlayer(video, v.src);
                    }
                } else if (video.readyState >= 2) {
                    wrapper.classList.add('video-loaded'); // Already loaded (e.g. navigated back)
                }
                
                // Ensure mobile configuration is applied
                this.configureVideoForMobile(video);

                // Apply global mute state to current video
                video.muted = this._globalMutedState;

                // Setup progress tracking for current video
                this.setupProgressTracking(video);

                // Lecture automatique si la vidéo est chargée
                if (video.readyState >= 2) {
                    video.play().catch(() => {});
                } else {
                    // Sinon, attendre qu'elle soit prête
                    const playOnLoad = () => {
                        video.play().catch(() => {});
                        video.removeEventListener('loadeddata', playOnLoad);
                    };
                    video.addEventListener('loadeddata', playOnLoad);
                }
            } else {
                video.pause();
                video.currentTime = 0;
            }
        });

        // Mettre à jour l'icône du bouton pause
        if (this.updatePauseButtonIcon) {
            this.updatePauseButtonIcon();
        }

        // Update all mute button icons
        this.updateAllMuteButtonIcons();
    }

    /**
     * Setup progress tracking for a video element
     * @param {HTMLVideoElement} video - The video element to track
     */
    setupProgressTracking(video) {
        // Remove existing event listeners to prevent duplicates
        video.removeEventListener('timeupdate', this._progressUpdateHandler);
        video.removeEventListener('loadedmetadata', this._progressUpdateHandler);
        
        // Create a bound event handler
        this._progressUpdateHandler = () => {
            this.updateProgressBar(video);
        };
        
        // Add event listeners
        video.addEventListener('timeupdate', this._progressUpdateHandler);
        video.addEventListener('loadedmetadata', this._progressUpdateHandler);
        
        // Initial update
        this.updateProgressBar(video);
    }

    /**
     * Dispatch VIDEO_CHANGED event when current video index changes.
     */
    dispatchVideoChanged() {
        const ev = new CustomEvent(ROCKETSTORIES_EVENTS.VIDEO_CHANGED, {
            bubbles: true,
            composed: true,
            detail: { videoIndex: this.currentVideoIndex }
        });
        this.dispatchEvent(ev);
    }

    /**
     * Navigate to previous video
     * Updates display and scrolls to new video
     */
    previousVideo() {
        if (this.currentVideoIndex > 0) {
            this.currentVideoIndex--;
            this.dispatchVideoChanged();
            this.updateDialogContent();
            this.showCurrentVideo();
        }
    }

    /**
     * Navigate to next video
     * Updates display and scrolls to new video
     */
    nextVideo() {
        if (this.currentVideoIndex < this.videos.length - 1) {
            this.currentVideoIndex++;
            this.dispatchVideoChanged();
            this.updateDialogContent();
            this.showCurrentVideo();
        }
    }

    /**
     * Pause all other main videos in hover mode
     * @param {HTMLVideoElement} currentVideo - The video that should keep playing
     */
    pauseOtherMainVideos(currentVideo) {
        const allMainVideos = this.shadowRoot.querySelectorAll('.video-main');
        allMainVideos.forEach(video => {
            if (video !== currentVideo && !video.paused) {
                video.pause();
            }
        });
    }

    /**
     * Setup hover preview behavior for a video item
     * @param {HTMLElement} videoItem - The video item element
     */
    setupHoverPreview(videoItem) {
        // Check if hover events are already set up for this item
        if (videoItem.dataset.hoverEnabled === 'true') {
            return; // Already set up, skip
        }

        // Remove existing hover event listeners to prevent duplicates
        videoItem.removeEventListener('mouseenter', videoItem._hoverEnterHandler);
        videoItem.removeEventListener('mouseleave', videoItem._hoverLeaveHandler);
        
        // Create hover event handlers
        videoItem._hoverEnterHandler = async (e) => {
            // Load preview video on first hover if not already loaded
            if (!videoItem.dataset.previewLoaded) {
                const videoId = videoItem.dataset.videoId;
                const video = this.videos.find(v => v.id === videoId);
                if (video && video.preview) {
                    await this.loadPreviewVideoOnHover(videoItem, video);
                } else {
                    console.log('No video found or no preview URL');
                }
            }

            // Start playing preview video on mouse enter
            const currentPreviewVideo = videoItem.querySelector('.video-preview');
            if (currentPreviewVideo) {
                // Wait a bit for the video to be ready
                setTimeout(() => {
                    currentPreviewVideo.play();
                }, 100);
            } else {
                console.log('No preview video element found');
            }
        };
        
        videoItem._hoverLeaveHandler = (e) => {
            // Pause preview video and reset to beginning on mouse leave
            const currentPreviewVideo = videoItem.querySelector('.video-preview');
            if (currentPreviewVideo) {
                currentPreviewVideo.pause();
                currentPreviewVideo.currentTime = 0;
            }
        };
        
        // Add hover event listeners
        videoItem.addEventListener('mouseenter', videoItem._hoverEnterHandler);
        videoItem.addEventListener('mouseleave', videoItem._hoverLeaveHandler);
        
        // Add click handler for hover mode to play main video instead of opening dialog
        if (this.previewMode) {
            videoItem._hoverClickHandler = async (e) => {
                e.preventDefault();
                e.stopPropagation();
                
                // Center the clicked video item in the carousel
                this.centerVideoItem(videoItem);
                
                // Check if main video already exists to prevent duplicate creation
                const existingMainVideo = videoItem.querySelector('.video-main');
                if (existingMainVideo) {
                    return; // Main video already exists, don't create another one
                }
                
                const videoId = videoItem.dataset.videoId;
                const video = this.videos.find(v => v.id === videoId);
                
                if (video && video.src) {
                    
                    const placeholder = videoItem.querySelector('.video-placeholder');
                    const previewVideo = videoItem.querySelector('.video-preview');
                    const posterImg = videoItem.querySelector('.poster-img');
                    
                    // Hide poster image and preview video
                    if (posterImg) posterImg.classList.add('hidden');
                    if (previewVideo) previewVideo.classList.add('hidden');
                    
                    // Create main video element to replace preview
                    const mainVideo = document.createElement('video');
                    mainVideo.className = 'video-main';
                    
                    // Setup video
                    mainVideo.src = video.src;
                    mainVideo.controls = true;
                    mainVideo.autoplay = true;
                    mainVideo.playsInline = true;
                    mainVideo.muted = false; // Enable sound for main video
                    
                    // Add to placeholder
                    placeholder.appendChild(mainVideo);
                    
                    // Setup HLS if needed
                    await this.loadHLS();
                    this.setupHLSPlayer(mainVideo, video.src);
                    
                    // Configure for mobile
                    this.configureVideoForMobile(mainVideo);
                    
                    // Pause all other main videos before starting this one
                    this.pauseOtherMainVideos(mainVideo);
                    
                    // Add play/pause button
                    const playPauseBtn = document.createElement('button');
                    playPauseBtn.className = 'video-play-pause-btn';
                    playPauseBtn.innerHTML = '<svg width="36" height="36"><use href="#icon-play"></use></svg>';
                    playPauseBtn.title = this.getLabel('play');
                    playPauseBtn.setAttribute('aria-label', this.getLabel('play'));
                    
                    // Add mute/unmute button
                    const muteBtn = document.createElement('button');
                    muteBtn.className = 'video-mute-btn';
                    muteBtn.innerHTML = '<svg width="36" height="36"><use href="#icon-unmute"></use></svg>';
                    muteBtn.title = this.getLabel('mute');
                    muteBtn.setAttribute('aria-label', this.getLabel('mute'));
                    
                    placeholder.appendChild(playPauseBtn);
                    placeholder.appendChild(muteBtn);
                    
                    // Play/pause handlers
                    const togglePlayPause = () => {
                        if (mainVideo.paused) {
                            mainVideo.play();
                        } else {
                            mainVideo.pause();
                        }
                    };
                    
                    const updatePlayPauseButton = () => {
                        const use = playPauseBtn.querySelector('use');
                        if (mainVideo.paused) {
                            use.setAttribute('href', '#icon-play');
                            // Show buttons when paused
                            playPauseBtn.classList.add('btn-visible');
                            playPauseBtn.classList.remove('btn-auto-hide');
                            muteBtn.classList.add('btn-visible');
                            muteBtn.classList.remove('btn-auto-hide');
                        } else {
                            use.setAttribute('href', '#icon-pause');
                            // Auto-hide buttons when playing (CSS handles hover)
                            setTimeout(() => {
                                if (!mainVideo.paused) {
                                    playPauseBtn.classList.remove('btn-visible');
                                    playPauseBtn.classList.add('btn-auto-hide');
                                    muteBtn.classList.remove('btn-visible');
                                    muteBtn.classList.add('btn-auto-hide');
                                }
                            }, 1000);
                        }
                    };

                    // Mute/unmute handlers
                    const toggleMute = () => {
                        mainVideo.muted = !mainVideo.muted;
                        this.updateMuteButton(muteBtn, mainVideo.muted);
                    };
                    
                    // Update button on play/pause events
                    mainVideo.addEventListener('play', updatePlayPauseButton);
                    mainVideo.addEventListener('pause', updatePlayPauseButton);
                    
                    // Show buttons on hover when video is playing (handled via CSS with btn-auto-hide class)
                    mainVideo.addEventListener('mouseenter', () => {
                        if (!mainVideo.paused) {
                            playPauseBtn.querySelector('use').setAttribute('href', '#icon-pause');
                        }
                    });
                    
                    // CSS handles the hover visibility via .video-placeholder:hover .btn-auto-hide
                    // No need for JS-based opacity changes
                    
                    playPauseBtn.addEventListener('click', (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        togglePlayPause();
                    });

                    playPauseBtn.addEventListener('keydown', (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            togglePlayPause();
                        }
                    });

                    muteBtn.addEventListener('click', (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleMute();
                    });

                    muteBtn.addEventListener('keydown', (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleMute();
                        }
                    });
                    
                    // Close video function (for escape key and video end)
                    const closeVideo = () => {
                        mainVideo.pause();
                        mainVideo.remove();
                        playPauseBtn.remove();
                        muteBtn.remove();
                        
                        // Restore preview video and poster
                        if (previewVideo) previewVideo.classList.remove('hidden');
                        if (posterImg) posterImg.classList.remove('hidden');
                        
                        // Remove escape handler
                        document.removeEventListener('keydown', escapeHandler);
                    };
                    
                    const escapeHandler = (e) => {
                        if (e.key === 'Escape') {
                            closeVideo();
                        }
                    };
                    
                    document.addEventListener('keydown', escapeHandler);
                    
                    // Pause other videos when this one starts playing
                    mainVideo.addEventListener('play', () => {
                        this.pauseOtherMainVideos(mainVideo);
                        // Centrage lors de la reprise de lecture en mode preview
                        if (this.previewMode) {
                            this.centerVideoItem(videoItem);
                        }
                    });
                    
                    // Close on video end
                    mainVideo.addEventListener('ended', closeVideo);
                    
                    // Toggle play/pause on video click (if not clicking controls)
                    mainVideo.addEventListener('click', (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        // Don't toggle if clicking on video controls
                        if (e.target === mainVideo && !e.target.controls) {
                            togglePlayPause();
                        }
                    });
                }
            };
            
            videoItem.addEventListener('click', videoItem._hoverClickHandler);
        }
        
        // Mark as hover-enabled
        videoItem.dataset.hoverEnabled = 'true';
    }

    /**
     * Setup click handler for preview mode (inline playback)
     * This is separate from hover preview - it only handles click behavior
     * @param {HTMLElement} videoItem - The video item element
     */
    setupPreviewModeClick(videoItem) {
        // Check if already set up
        if (videoItem.dataset.previewModeEnabled === 'true') {
            return;
        }

        // Add click handler for preview mode to play main video instead of opening dialog
        videoItem._previewModeClickHandler = async (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            // Center the clicked video item in the carousel
            this.centerVideoItem(videoItem);
            
            // Check if main video already exists to prevent duplicate creation
            const existingMainVideo = videoItem.querySelector('.video-main');
            if (existingMainVideo) {
                return; // Main video already exists, don't create another one
            }
            
            const videoId = videoItem.dataset.videoId;
            const video = this.videos.find(v => v.id === videoId);
            
            if (video && video.src) {
                const placeholder = videoItem.querySelector('.video-placeholder');
                const previewVideo = videoItem.querySelector('.video-preview');
                const posterImg = videoItem.querySelector('.poster-img');
                
                // Hide poster image and preview video
                if (posterImg) posterImg.classList.add('hidden');
                if (previewVideo) {
                    previewVideo.pause();
                    previewVideo.classList.add('hidden');
                }
                
                // Create main video element to replace preview
                const mainVideo = document.createElement('video');
                mainVideo.className = 'video-main';
                
                // Setup video
                mainVideo.src = video.src;
                mainVideo.controls = true;
                mainVideo.autoplay = true;
                mainVideo.playsInline = true;
                mainVideo.muted = false; // Enable sound for main video
                
                // Add to placeholder
                placeholder.appendChild(mainVideo);
                
                // Setup HLS if needed
                await this.loadHLS();
                this.setupHLSPlayer(mainVideo, video.src);
                
                // Configure for mobile
                this.configureVideoForMobile(mainVideo);
                
                // Pause all other main videos before starting this one
                this.pauseOtherMainVideos(mainVideo);
                
                // Add play/pause button
                const playPauseBtn = document.createElement('button');
                playPauseBtn.className = 'video-play-pause-btn';
                playPauseBtn.innerHTML = '<svg width="36" height="36"><use href="#icon-play"></use></svg>';
                playPauseBtn.title = this.getLabel('play');
                playPauseBtn.setAttribute('aria-label', this.getLabel('play'));
                
                // Add mute/unmute button
                const muteBtn = document.createElement('button');
                muteBtn.className = 'video-mute-btn';
                muteBtn.innerHTML = '<svg width="36" height="36"><use href="#icon-unmute"></use></svg>';
                muteBtn.title = this.getLabel('mute');
                muteBtn.setAttribute('aria-label', this.getLabel('mute'));
                
                placeholder.appendChild(playPauseBtn);
                placeholder.appendChild(muteBtn);
                
                // Play/pause handlers
                const togglePlayPause = () => {
                    if (mainVideo.paused) {
                        mainVideo.play();
                    } else {
                        mainVideo.pause();
                    }
                };
                
                const updatePlayPauseButton = () => {
                    const use = playPauseBtn.querySelector('use');
                    if (mainVideo.paused) {
                        use.setAttribute('href', '#icon-play');
                        // Show buttons when paused
                        playPauseBtn.classList.add('btn-visible');
                        playPauseBtn.classList.remove('btn-auto-hide');
                        muteBtn.classList.add('btn-visible');
                        muteBtn.classList.remove('btn-auto-hide');
                    } else {
                        use.setAttribute('href', '#icon-pause');
                        // Auto-hide buttons when playing (CSS handles hover)
                        setTimeout(() => {
                            if (!mainVideo.paused) {
                                playPauseBtn.classList.remove('btn-visible');
                                playPauseBtn.classList.add('btn-auto-hide');
                                muteBtn.classList.remove('btn-visible');
                                muteBtn.classList.add('btn-auto-hide');
                            }
                        }, 1000);
                    }
                };

                // Mute/unmute handlers
                const toggleMute = () => {
                    mainVideo.muted = !mainVideo.muted;
                    this.updateMuteButton(muteBtn, mainVideo.muted);
                };
                
                // Update button on play/pause events
                mainVideo.addEventListener('play', updatePlayPauseButton);
                mainVideo.addEventListener('pause', updatePlayPauseButton);
                
                // Show buttons on hover when video is playing (handled via CSS with btn-auto-hide class)
                mainVideo.addEventListener('mouseenter', () => {
                    if (!mainVideo.paused) {
                        playPauseBtn.querySelector('use').setAttribute('href', '#icon-pause');
                    }
                });
                
                // CSS handles the hover visibility via .video-placeholder:hover .btn-auto-hide
                // No need for JS-based opacity changes
                
                playPauseBtn.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    togglePlayPause();
                });

                playPauseBtn.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        togglePlayPause();
                    }
                });

                muteBtn.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleMute();
                });

                muteBtn.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleMute();
                    }
                });
                
                // Close video function (for escape key and video end)
                const closeVideo = () => {
                    mainVideo.pause();
                    mainVideo.remove();
                    playPauseBtn.remove();
                    muteBtn.remove();
                    
                    // Restore preview video and poster
                    if (previewVideo) {
                        previewVideo.classList.remove('hidden');
                        previewVideo.play().catch(() => {});
                    }
                    if (posterImg) posterImg.classList.remove('hidden');
                    
                    // Remove escape handler
                    document.removeEventListener('keydown', escapeHandler);
                };
                
                const escapeHandler = (e) => {
                    if (e.key === 'Escape') {
                        closeVideo();
                    }
                };
                
                document.addEventListener('keydown', escapeHandler);
                
                // Pause other videos when this one starts playing
                mainVideo.addEventListener('play', () => {
                    this.pauseOtherMainVideos(mainVideo);
                    // Centrage lors de la reprise de lecture en mode preview
                    this.centerVideoItem(videoItem);
                });
                
                // Close on video end
                mainVideo.addEventListener('ended', closeVideo);
                
                // Toggle play/pause on video click
                mainVideo.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (e.target === mainVideo && !e.target.controls) {
                        togglePlayPause();
                    }
                });
            }
        };
        
        videoItem.addEventListener('click', videoItem._previewModeClickHandler);
        
        // Mark as preview-mode-enabled
        videoItem.dataset.previewModeEnabled = 'true';
    }

    /**
     * Apply shape styling to video items
     * Supports bubble, vertical, and horizontal shapes
     */
    applyShape() {
        const videoItems = this.shadowRoot.querySelectorAll('.video-item');
        const shapeClasses = ['shape-bubble', 'shape-vertical', 'shape-horizontal'];
        videoItems.forEach(item => {
            item.classList.remove(...shapeClasses);
            item.classList.add(`shape-${this.shape}`);
            
            // Add preview-mode class if component is in preview mode
            if (this.previewMode) {
                item.classList.add('preview-mode');
            } else {
                item.classList.remove('preview-mode');
            }
        });
        
        // Also apply shape to modal video items if they exist
        const modalVideoItems = this.shadowRoot.querySelectorAll('.modal-video-item');
        modalVideoItems.forEach(item => {
            item.classList.remove(...shapeClasses);
            if (this.shape !== 'bubble') {
                item.classList.add(`shape-${this.shape}`);
            }
        });
    }

    /**
     * Update navigation button icons based on modal direction
     * Changes arrow direction for vertical vs horizontal navigation
     */
    updateNavigationButtonIcons() {
        const currentVideo = this.shadowRoot.querySelector(`.modal-video-main[data-video-index="${this.currentVideoIndex}"]`);
        if (currentVideo) {
            const wrapper = currentVideo.closest('.video-wrapper');
            if (wrapper) {
                const prevBtn = wrapper.querySelector('.prev-button');
                const nextBtn = wrapper.querySelector('.next-button');
        
                if (prevBtn && nextBtn) {
                    const prevUse = prevBtn.querySelector('use');
                    const nextUse = nextBtn.querySelector('use');
                    if (this.modalDirection === 'vertical') {
                        if (prevUse) prevUse.setAttribute('href', '#icon-up');
                        if (nextUse) nextUse.setAttribute('href', '#icon-down');
                        prevBtn.title = this.getLabel('prevVertical');
                        nextBtn.title = this.getLabel('nextVertical');
                        prevBtn.setAttribute('aria-label', this.getLabel('prevVertical'));
                        nextBtn.setAttribute('aria-label', this.getLabel('nextVertical'));
                    } else {
                        if (prevUse) prevUse.setAttribute('href', '#icon-left');
                        if (nextUse) nextUse.setAttribute('href', '#icon-right');
                        prevBtn.title = this.getLabel('prevHorizontal');
                        nextBtn.title = this.getLabel('nextHorizontal');
                        prevBtn.setAttribute('aria-label', this.getLabel('prevHorizontal'));
                        nextBtn.setAttribute('aria-label', this.getLabel('nextHorizontal'));
                    }
                }
            }
        }
    }

    /**
     * Apply modal direction styling to navigation buttons
     * Supports horizontal and vertical navigation layouts
     */
    applyModalDirection() {
        const navigationButtons = this.shadowRoot.querySelector('.navigation-buttons');
        if (navigationButtons) {
            navigationButtons.classList.remove('direction-horizontal', 'direction-vertical');
            navigationButtons.classList.add(`direction-${this.modalDirection}`);
        }

        // Update button icons when direction is applied
        this.updateNavigationButtonIcons();
    }

    /**
     * Apply thumbnail border color from attribute to CSS variable
     */
    applyThumbnailStyles() {
        const color = this.getAttribute('thumbnail-border-color') || '#000000';
        this.style.setProperty('--thumbnail-border-color', color);
    }

    /**
     * Force mobile configuration for all videos in the component
     * Called after component is fully rendered
     */
    forceMobileConfiguration() {
        const allVideos = this.shadowRoot.querySelectorAll('video');
        allVideos.forEach(video => {
            this.configureVideoForMobile(video);
        });
        
        // Also configure any videos that might be added later
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        let videos = [];
                        if (node.querySelectorAll) {
                            videos = Array.from(node.querySelectorAll('video'));
                        }
                        if (node.tagName === 'VIDEO') {
                            videos.push(node);
                        }
                        videos.forEach(video => {
                            this.configureVideoForMobile(video);
                        });
                    }
                });
            });
        });
        
        observer.observe(this.shadowRoot, {
            childList: true,
            subtree: true
        });
    }

    /**
     * Setup carousel navigation buttons
     * Handles scroll functionality for preview video carousel
     */
    setupCarouselNavigation() {
        const prevBtn = this.shadowRoot.querySelector('.carousel-prev-btn');
        const nextBtn = this.shadowRoot.querySelector('.carousel-next-btn');
        const carouselContainer = this.shadowRoot.querySelector('.carousel-container');
        const carouselWrapper = this.shadowRoot.querySelector('.carousel-wrapper');

        if (!prevBtn || !nextBtn || !carouselContainer) return;

        // Apply direction class to wrapper and container for vertical carousels
        if (this.shape === 'vertical') {
            carouselWrapper.classList.add('direction-vertical');
            carouselContainer.classList.add('direction-vertical');
        } else {
            carouselWrapper.classList.remove('direction-vertical');
            carouselContainer.classList.remove('direction-vertical');
        }

        // Update navigation button visibility
        this.updateCarouselNavigationButtons();

        // Previous button click handler
        prevBtn.addEventListener('click', () => {
            this.scrollCarousel('prev');
        });

        // Next button click handler
        nextBtn.addEventListener('click', () => {
            this.scrollCarousel('next');
        });

        // Keyboard navigation for carousel
        carouselContainer.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                e.preventDefault();
                this.scrollCarousel('prev');
            } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                e.preventDefault();
                this.scrollCarousel('next');
            }
        });

        // Update button visibility on scroll
        carouselContainer.addEventListener('scroll', () => {
            this.updateCarouselNavigationButtons();
        });

        // Update is-centered on resize (overflow can change)
        this._carouselResizeHandler = () => {
            this.updateCarouselNavigationButtons();
        };
        window.addEventListener('resize', this._carouselResizeHandler);
    }

    /**
     * Scroll carousel in specified direction
     * @param {string} direction - 'prev' or 'next'
     */
    scrollCarousel(direction) {
        const carouselContainer = this.shadowRoot.querySelector('.carousel-container');
        if (!carouselContainer) return;

        const isVertical = carouselContainer.classList.contains('direction-vertical');
        const scrollAmount = 200;

        if (isVertical) {
            if (direction === 'prev') {
                carouselContainer.scrollTop -= scrollAmount;
            } else {
                carouselContainer.scrollTop += scrollAmount;
            }
        } else {
            if (direction === 'prev') {
                carouselContainer.scrollLeft -= scrollAmount;
            } else {
                carouselContainer.scrollLeft += scrollAmount;
            }
        }
    }

    /**
     * Center a specific video item in the carousel
     * @param {HTMLElement} videoItem - The video item to center
     */
    centerVideoItem(videoItem) {
        const carouselContainer = this.shadowRoot.querySelector('.carousel-container');
        if (!carouselContainer || !videoItem) return;

        const containerRect = carouselContainer.getBoundingClientRect();
        const itemRect = videoItem.getBoundingClientRect();
        
        // Check if carousel is vertical or horizontal
        const isVertical = carouselContainer.classList.contains('direction-vertical');
        
        if (isVertical) {
            // For vertical carousel, center vertically
            const itemCenter = itemRect.top + itemRect.height / 2;
            const containerCenter = containerRect.top + containerRect.height / 2;
            const scrollOffset = itemCenter - containerCenter;
            
            carouselContainer.scrollBy({
                top: scrollOffset,
                behavior: 'smooth'
            });
        } else {
            // For horizontal carousel, center horizontally
            const itemCenter = itemRect.left + itemRect.width / 2;
            const containerCenter = containerRect.left + containerRect.width / 2;
            const scrollOffset = itemCenter - containerCenter;
            
            carouselContainer.scrollBy({
                left: scrollOffset,
                behavior: 'smooth'
            });
        }
    }

    /**
     * Update carousel navigation button visibility
     * Hides buttons when scrolling is not possible
     */
    updateCarouselNavigationButtons() {
        const prevBtn = this.shadowRoot.querySelector('.carousel-prev-btn');
        const nextBtn = this.shadowRoot.querySelector('.carousel-next-btn');
        const carouselContainer = this.shadowRoot.querySelector('.carousel-container');

        if (!prevBtn || !nextBtn || !carouselContainer) return;
        
        const canScrollLeft = carouselContainer.scrollLeft > 0;
        const canScrollRight = carouselContainer.scrollLeft < (carouselContainer.scrollWidth - carouselContainer.clientWidth);

        const isVertical = carouselContainer.classList.contains('direction-vertical');
        const canScrollPrev = isVertical
            ? carouselContainer.scrollTop > 0
            : canScrollLeft;
        const canScrollNext = isVertical
            ? carouselContainer.scrollTop < (carouselContainer.scrollHeight - carouselContainer.clientHeight)
            : canScrollRight;

        prevBtn.classList.toggle('hidden', !canScrollPrev);
        nextBtn.classList.toggle('hidden', !canScrollNext);

        // has-outline: when thumbnails have border
        if (this.hasAttribute('show-thumbnail-border')) {
            carouselContainer.classList.add('has-outline');
        } else {
            carouselContainer.classList.remove('has-outline');
        }

        // is-centered: center content when it doesn't overflow
        const isOverflowing = isVertical
            ? carouselContainer.scrollHeight > carouselContainer.clientHeight
            : carouselContainer.scrollWidth > carouselContainer.clientWidth;
        carouselContainer.classList.toggle('is-centered', !isOverflowing);
    }

    /**
     * Setup scroll detection for widget appearance
     */
    setupScrollDetection() {
        this._widgetVisible = false;
        this.hideWidget();
        
        // Single scroll handler
        this._scrollHandler = () => {
            if (this._scrollTimeout) clearTimeout(this._scrollTimeout);
            this._scrollTimeout = setTimeout(() => this.showWidget(), this.widgetDelay);
        };
        
        window.addEventListener('scroll', this._scrollHandler, { passive: true });
    }

    /**
     * Cleanup scroll detection
     */
    cleanupScrollDetection() {
        if (this._scrollHandler) {
            window.removeEventListener('scroll', this._scrollHandler);
            this._scrollHandler = null;
        }
        if (this._scrollTimeout) {
            clearTimeout(this._scrollTimeout);
            this._scrollTimeout = null;
        }
    }

    /**
     * Show the widget with CSS class transition
     */
    async showWidget() {
        // Prevent showing if session indicates widget was closed
        if (this.widgetRememberClose && sessionStorage.getItem('videoWidgetClosed') === '1') return;
        if (this._widgetVisible) return;
        
        const mini = this.shadowRoot.querySelector('.mini');
        if (mini) {
            this._widgetVisible = true;
            
            // Load widget content before showing
            await this.loadWidgetContent();
            
            // Add 'show' class to trigger CSS transition
            mini.classList.add('show');
        }
    }

    /**
     * Hide the widget with CSS class transition
     */
    hideWidget() {
        const mini = this.shadowRoot.querySelector('.mini');
        if (mini) {
            this._widgetVisible = false;
            // Remove 'show' class to trigger CSS transition
            mini.classList.remove('show');
        }
    }

    /**
     * Close the widget permanently (user clicked close button)
     */
    closeWidget() {
        // Close the modal if open
        if (this._modalWidgetActive) {
            const dialog = this.shadowRoot.querySelector('.modal-dialog.widget-mode');
            if (dialog) {
                dialog.close();
            }
            this._modalWidgetActive = false;
        }
        
        // Hide the widget
        this.hideWidget();
        
        // Remember close state if enabled
        if (this.widgetRememberClose) {
            sessionStorage.setItem('videoWidgetClosed', '1');
        }
        
        // Re-enable page scroll
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.top = '';
    }

    /**
     * Load widget content when it appears
     */
    async loadWidgetContent() {
        const miniVideo = this.shadowRoot.querySelector('.mini-video');
        const miniPlaceholder = this.shadowRoot.querySelector('.mini .placeholder');
        
        if (!miniVideo || !miniPlaceholder) return;
        
        // Get video data
        let src = this.getAttribute('src') || '';
        let poster = this.getAttribute('poster') || '';
        let preview = '';

        // If we have JSON data, use the first video
        if (this.videos && this.videos.length > 0) {
            const firstVideo = this.videos[0];
            src = firstVideo.src || src;
            poster = firstVideo.poster || poster;
            preview = firstVideo.preview || '';
        }

        // Check user's motion preference
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        // Check connection quality
        const isGoodConnection = await this.checkConnectionQuality();
        
        // Show poster image if available
        if (poster) {
            miniVideo.classList.remove('hidden');
            miniVideo.poster = poster;
            miniPlaceholder.classList.add('hidden');
        } else {
            miniVideo.classList.add('hidden');
            miniPlaceholder.classList.remove('hidden');
        }
        
        // Show preview video if conditions are met
        if (preview && !prefersReducedMotion && isGoodConnection) {
            miniVideo.src = preview;
            miniVideo.muted = true;
            miniVideo.loop = true;
            miniVideo.playsInline = true;
            miniVideo.autoplay = true;
        }
    }

    /**
     * Update mute button state - unified for carousel and widget
     * @param {HTMLButtonElement} muteBtn - The mute button element
     * @param {boolean} isMuted - Whether the video is muted
     */
    updateMuteButton(muteBtn, isMuted) {
        if (!muteBtn) return;
        const use = muteBtn.querySelector('use');
        if (use) {
            use.setAttribute('href', isMuted ? '#icon-mute' : '#icon-unmute');
        }
        muteBtn.title = this.getLabel(isMuted ? 'unmute' : 'mute');
        muteBtn.setAttribute('aria-label', this.getLabel(isMuted ? 'unmute' : 'mute'));
    }

    /**
     * Update pause button state - unified for carousel and widget
     * @param {HTMLButtonElement} pauseBtn - The pause button element
     * @param {boolean} isPaused - Whether the video is paused
     */
    updatePauseButton(pauseBtn, isPaused) {
        if (!pauseBtn) return;
        const use = pauseBtn.querySelector('use');
        if (use) {
            use.setAttribute('href', isPaused ? '#icon-play' : '#icon-pause');
        }
        pauseBtn.title = this.getLabel(isPaused ? 'play' : 'pause');
        pauseBtn.setAttribute('aria-label', this.getLabel(isPaused ? 'play' : 'pause'));
    }

    /**
     * Get localized label from the video-carousel-labels template
     * Labels are defined in Liquid and can be translated
     * @param {string} key - The label key (e.g., 'play', 'pause', 'mute')
     * @returns {string} The localized label or the key itself if not found
     */
    getLabel(key) {
        if (!this._labels) {
            const template = document.getElementById('video-carousel-labels');
            this._labels = {};
            if (template) {
                template.content.querySelectorAll('[data-label]').forEach(el => {
                    this._labels[el.dataset.label] = el.textContent.trim();
                });
            }
        }
        // Return the label or the key itself as fallback (helps identify missing translations)
        return this._labels[key] || `[${key}]`;
    }
}

// Static property for shared HLS loading promise across all instances
VideoCarousel._hlsLoadingPromise = null;

// Register the custom element
customElements.define('video-carousel', VideoCarousel);

} // End of if (!customElements.get('video-carousel'))

/**
 * Charge un fichier SVG et l'injecte inline dans un élément cible
 * @param {string} url - Chemin du fichier SVG
 * @param {HTMLElement} target - Élément dans lequel injecter le SVG
 * @returns {Promise}
 */
if (typeof window.loadAndInjectSVG === 'undefined') {
    window.loadAndInjectSVG = async function(url, target) {
        const response = await fetch(url);
        const svgText = await response.text();
        const temp = document.createElement('div');
        temp.innerHTML = svgText;
        const svg = temp.querySelector('svg');
        if (svg) {
            svg.setAttribute('width', '24');
            svg.setAttribute('height', '24');
            svg.setAttribute('aria-hidden', 'true');
            target.innerHTML = '';
            target.appendChild(svg);
        }
    };
}

// Shopify Theme Editor event handlers for live preview updates
document.addEventListener('shopify:section:load', function(event) {
    // Find video-carousel elements in the loaded section
    const section = event.target;
    const carousels = section.querySelectorAll('video-carousel');
    
    carousels.forEach(carousel => {
        // Force re-render by triggering connectedCallback
        if (carousel.isConnected) {
            carousel.render();
            carousel.collectVideos().then(() => {
                if (carousel.isWidgetMode()) {
                    if (carousel.widgetShowOnScroll) {
                        carousel.setupScrollDetection();
                    } else {
                        carousel.hideWidget();
                        setTimeout(() => carousel.showWidget(), carousel.widgetDelay);
                    }
                }
            });
        }
    });
});

document.addEventListener('shopify:section:unload', function(event) {
    // Cleanup video-carousel elements in the unloaded section
    const section = event.target;
    const carousels = section.querySelectorAll('video-carousel');
    
    carousels.forEach(carousel => {
        // Close any open dialogs
        const dialog = carousel.shadowRoot?.querySelector('dialog');
        if (dialog && dialog.open) {
            dialog.close();
        }
        // Hide widget if visible
        const mini = carousel.shadowRoot?.querySelector('.mini');
        if (mini) {
            mini.classList.remove('show');
        }
    });
});

document.addEventListener('shopify:section:select', function(event) {
    // When section is selected in editor, ensure widget is visible for preview
    const section = event.target;
    const carousels = section.querySelectorAll('video-carousel');
    
    carousels.forEach(carousel => {
        if (carousel.isWidgetMode()) {
            carousel.showWidget();
        }
    });
});

document.addEventListener('shopify:section:deselect', function(event) {
    // Optional: handle section deselection if needed
});